import { SUPPORTED_CV_LANGUAGES } from '~/constants/cvLanguages'
import { PDF_TEMPLATES } from '~/constants/cvTemplates'
import { DAILY_LIMITS, FREE_PLAN, MAX_APPLICATIONS } from '~/constants/planLimits'

/**
 * Free account vs Job Search Pack, row by row, for the comparison table on
 * /pricing (components/homepage/FeatureComparisonSection.vue). The texts are
 * under `premium.comparison.rows.<id>`; every number in them comes from
 * constants/planLimits.ts, the limits the server meters with, so the table
 * cannot drift from what the app does.
 *
 * Rules the rows follow:
 * - `free: 'no'` only for what a free account really cannot do; a feature
 *   with a daily or lifetime allowance is 'limited' and says how much.
 * - `fairUse` marks premium features metered by a daily ceiling
 *   (DAILY_LIMITS[...].premium); the table shows "Unlimited*" and the
 *   footnote explains it. Never write "unlimited" without it.
 * - The yearly plan has everything the pack has, so it is not a column.
 */
export type FreeAccess = 'yes' | 'limited' | 'no'

export interface ComparisonRow {
  id: string
  free: FreeAccess
  /** A rows.<id>.free text exists; otherwise only the mark is shown. */
  freeText?: boolean
  /** A rows.<id>.pack text exists; otherwise "Unlimited*" (fairUse) or the mark. */
  packText?: boolean
  fairUse?: boolean
  /** Interpolated into the row's texts. */
  params?: Record<string, number>
}

export interface ComparisonGroup {
  id: string
  icon: string
  rows: ComparisonRow[]
}

const limit = DAILY_LIMITS

export const PLAN_COMPARISON: ComparisonGroup[] = [
  {
    id: 'cv',
    icon: 'bi-file-earmark-person',
    rows: [
      { id: 'cvs', free: 'limited', freeText: true, packText: true, params: { count: FREE_PLAN.cvs } },
      { id: 'templates', free: 'yes', freeText: true, packText: true, params: { count: PDF_TEMPLATES.length } },
      { id: 'languages', free: 'yes', params: { count: SUPPORTED_CV_LANGUAGES.length } },
      { id: 'duplicate', free: 'no' },
    ],
  },
  {
    id: 'pdf',
    icon: 'bi-file-earmark-pdf',
    rows: [
      { id: 'download', free: 'limited', freeText: true, packText: true, params: { count: limit['pdf-export'].free } },
      { id: 'clean', free: 'limited', freeText: true, packText: true },
      { id: 'coverLetterPdf', free: 'limited', freeText: true, packText: true },
    ],
  },
  {
    id: 'ai',
    icon: 'bi-stars',
    rows: [
      { id: 'chat', free: 'limited', freeText: true, fairUse: true, params: { count: limit['ai-chat-build'].free } },
      { id: 'import', free: 'limited', freeText: true, fairUse: true, params: { count: limit['ai-import'].free } },
      { id: 'suggestions', free: 'limited', freeText: true, fairUse: true, params: { count: limit['ai-writing'].free } },
      { id: 'aboutMe', free: 'limited', freeText: true, fairUse: true, params: { count: limit['ai-about-me'].free } },
      { id: 'rewrite', free: 'no', fairUse: true },
      { id: 'translate', free: 'limited', freeText: true, fairUse: true, params: { count: FREE_PLAN.translations } },
    ],
  },
  {
    id: 'apply',
    icon: 'bi-briefcase-fill',
    rows: [
      { id: 'jobMatch', free: 'limited', freeText: true, packText: true, fairUse: true, params: { count: limit['job-match'].free, items: FREE_PLAN.reportItems } },
      { id: 'atsUpload', free: 'limited', freeText: true, packText: true, fairUse: true, params: { count: limit['ats-upload'].free, items: FREE_PLAN.reportItems } },
      { id: 'atsEditor', free: 'no', fairUse: true },
      { id: 'coverLetters', free: 'limited', freeText: true, fairUse: true, params: { count: FREE_PLAN.coverLetters, drafts: FREE_PLAN.coverLetterDrafts } },
      { id: 'interviewPrep', free: 'limited', freeText: true, fairUse: true, params: { count: limit['interview-prep'].free } },
      { id: 'applicationEmail', free: 'no', fairUse: true },
      { id: 'linkTracking', free: 'no', packText: true },
      { id: 'tracker', free: 'yes', freeText: true, packText: true, params: { count: MAX_APPLICATIONS } },
    ],
  },
  {
    id: 'online',
    icon: 'bi-globe2',
    rows: [
      { id: 'web', free: 'no', freeText: true, packText: true },
      { id: 'stats', free: 'no' },
    ],
  },
]

/** What a visitor without an account gets, for the note under the table. */
export const ANONYMOUS_LIMITS = {
  chat: DAILY_LIMITS['ai-chat-build'].anonymous,
  pdf: DAILY_LIMITS['pdf-export'].anonymous,
  ats: DAILY_LIMITS['ats-upload'].anonymous,
} as const
